import {useState, useEffect, useCallback, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {KP11LinkCouponService} from './kp11-coupon-service'

import {isCartEmpty} from './kp11-utility'
import {
  SOCIAL_ADS_5_CODES,
  SOCIAL_ADS_4_CODES,
} from '../../../../utils/constants'
import {selectDefaultCartCentersNApps} from '../../../../app-store/kp11CartSlice'
import {selectKP11SelectCartCentersNApps} from '../../../../app-store/kp11CartSlice'
import {
  setCouponData,
  selectedCouponData,
} from '../../../../app-store/kp11CouponSlice'
import {useAuth0} from '@auth0/auth0-react'
import {getUserRoles} from '../../../../utils/get-user-roles'

export function KP11LinkUseCoupons(
  client,
  locationState,
  selectedCountry = 'US',
  thryvAccount,
) {
  // Redux selector for cart
  const structuredCart = useSelector(selectKP11SelectCartCentersNApps)
  const [newMC, setNewMC] = useState(false)
  const [defaultObFeeCoupon, setDefaultObFeeCoupon] = useState([]) // OBFEE
  const [plansIncentive, setPlansIncentive] = useState([]) // New customer
  const [plansIncentiveGroups, setPlansIncentiveGroups] = useState({})
  const [promotions, setPromotions] = useState([]) // Manual/custom
  const defaultCartCentersNApps = useSelector(selectDefaultCartCentersNApps)
  const allCoupons = useSelector(selectedCouponData)
  const dispatch = useDispatch()
  // In-memory cache for coupon validation results
  const couponValidationCache = useRef({})
  const {user} = useAuth0()
  const userRoles = getUserRoles(user)
  console.log('userRoles :: ', userRoles)

  const validateAndApplyPlansIncentive = useCallback(
    async groupedCoupons => {
      let validCoupons = []
      const seenCouponCodes = new Set()

      for (let key of Object.keys(groupedCoupons)) {
        let selectedCoupons = groupedCoupons[key].filter(item => item.isChecked)
        if (selectedCoupons.length > 0) {
          for (let coupon of selectedCoupons) {
            // Skip if this coupon code was already processed in this run
            if (seenCouponCodes.has(coupon.coupon_code)) continue

            const planCode =
              coupon.plan_code ||
              (coupon.bundle_config ? coupon.bundle_config.split(',')[0] : '')
            const cacheKey = `${coupon.coupon_code}|${selectedCountry}|${planCode}`
            let response = couponValidationCache.current[cacheKey]
            if (!response) {
              response = await KP11LinkCouponService.validateCouponByPlans(
                client,
                coupon.coupon_code,
                [coupon.plan_code || coupon.bundle_config.split(',')[0]],
                selectedCountry,
              )
              // Cache only valid responses
              if (response) {
                couponValidationCache.current[cacheKey] = response
              }
            }
            if (response?.isValid) {
              seenCouponCodes.add(coupon.coupon_code)
              validCoupons.push(response)
            }
          }
        }
      }

      setPlansIncentive(validCoupons)
    },
    [client, selectedCountry],
  )

  const togglePlanIncentive = useCallback(
    async (planDesc, couponCode, checked) => {
      setPlansIncentiveGroups(prev => {
        const updated = {
          ...prev,
          [planDesc]: prev[planDesc].map(c =>
            c.coupon_code === couponCode ? {...c, isChecked: checked} : c,
          ),
        }

        validateAndApplyPlansIncentive(updated)

        return updated
      })
    },
    [validateAndApplyPlansIncentive],
  )

  // Remove coupon
  const removeCoupon = useCallback(couponCode => {
    setDefaultObFeeCoupon(prev => prev.filter(c => c.code !== couponCode))
    setPlansIncentive(prev => prev.filter(c => c.code !== couponCode))
    setPromotions(prev => prev.filter(c => c.code !== couponCode))

    // also uncheck it in groups (modal state)
    setPlansIncentiveGroups(prev => {
      const updated = {...prev}
      for (let planDesc of Object.keys(updated)) {
        updated[planDesc] = updated[planDesc].map(c =>
          c.coupon_code === couponCode ? {...c, isChecked: false} : c,
        )
      }
      return updated
    })
  }, [])

  // Apply promo codes
  const fetchAndApplyPromoCode = useCallback(coupon => {
    setPromotions(prev => [...prev, coupon])
  }, [])

  //   console on allcoupouns update
  useEffect(() => {
    console.log('all coupons', allCoupons)
  }, [allCoupons])

  //check if MC is added new
  useEffect(() => {
    const isMCInCart = structuredCart?.centers?.find(
      ({code}) =>
        code === 'mc_plus-5-thryv-m2m' || code === 'mc_pro-5-thryv-m2m',
    )
    const isMCInDefault = defaultCartCentersNApps?.centers?.some(
      ({code}) =>
        code === 'mc_plus-5-thryv-m2m' || code === 'mc_pro-5-thryv-m2m',
    )
    let isNewMC = false
    if (isMCInCart && isMCInCart.isAlreadyAdded !== true && !isMCInDefault) {
      isNewMC = true
    }
    setNewMC(isNewMC)
  }, [defaultCartCentersNApps?.centers, structuredCart])

  //  Apply OBFEE on init
  useEffect(() => {
    async function fetchObFeeValidateAndApply() {
      const obFee = {
        code: 'keap-partner-waive-obfee',
        description: 'Onboarding Fee Waiver',
      }
      const res = await KP11LinkCouponService.validateObFeeCoupon(
        client,
        obFee.code,
        selectedCountry,
      )
      if (res?.isValid) setDefaultObFeeCoupon([res])
    }
    if (!locationState?.isUpgrade || newMC) {
      if (!userRoles.includes('InfluencerOps')) {
        fetchObFeeValidateAndApply()
      }
    }
  }, [client, locationState?.isUpgrade, newMC, selectedCountry])

  // Watch cart and apply/remove new customer incentive
  useEffect(() => {
    async function handleIncentivesByPlans() {
      if (isCartEmpty(structuredCart)) {
        // if (CouponService.isCartEmpty(cart)) {
        setPlansIncentive([])
        setPlansIncentiveGroups([])

        return
      }

      let codes = [
        ...(structuredCart.centers?.filter(c => !c.included).map(c => c.code) ||
          []),
        ...(structuredCart.apps?.filter(a => !a.included).map(a => a.code) ||
          []),
        // ...(cart.apps?.flatMap(a => {
        //   if (['addon-5-mcexpangr', 'addon-5-mcstartgr'].includes(a.code)) {
        //     const code = (a.addons || [])
        //       .filter(ad => ad.code === 'appmarket-5-thryv-m2m')
        //       .map(ad => ad.code)
        //     return code
        //   }
        //   return [a.code]
        // }) || []),
      ]
      let addonCodes = []
      structuredCart.apps?.forEach(app => {
        if (app?.addons) {
          app.addons.forEach(addon => {
            addonCodes.push(addon.code)
          })
        }
      })
      structuredCart.centers?.forEach(center => {
        if (center?.addons) {
          center.addons.forEach(addon => {
            addonCodes.push(addon.code)
          })
        }
      })
      codes = [...new Set([...codes, ...addonCodes])]

      console.log('filtered codes for plans incentives', codes)

      const response = await KP11LinkCouponService.fetchIncentivesByPlans(
        client,
        codes,
        selectedCountry,
        'null',
        thryvAccount,
      )
      console.log(
        '<<<<<< fetchIncentivesByPlans >>>>>> :: ' + JSON.stringify(response),
      )
      if (response && response.length > 0) {
        let groupedCoupons = {}
        for (let coupon of response) {
          // Check if coupon is applicable, based on the new client only flag
          if (coupon?.new_client_only_flag === 'Y') {
            if (!locationState?.isUpgrade) coupon['couponApplicable'] = true
            else coupon['couponApplicable'] = false
          } else {
            coupon['couponApplicable'] = true
          }

          // Check if the coupon is auto applicable or not and add isChecked attribute accordingly
          if (coupon.new_auto_apply === 'Y') {
            // BUSREQ-13923/13924: do not auto-apply Social Ads coupon if client already has any Social Ads
            const isSocialAdsCoupon = [
              ...SOCIAL_ADS_5_CODES,
              ...SOCIAL_ADS_4_CODES,
            ].includes(coupon.plan_code)
            const hasExistingSocialAds =
              isSocialAdsCoupon &&
              defaultCartCentersNApps?.apps?.some(({code}) =>
                [...SOCIAL_ADS_5_CODES, ...SOCIAL_ADS_4_CODES].includes(code),
              )
            coupon['isChecked'] = !hasExistingSocialAds
          } else {
            coupon['isChecked'] = false
          }
          coupon['isDisabled'] = false
        }
        const couponsTobeDisplayed = response.filter(
          coupon => coupon.couponApplicable,
        )
        // Group the coupons based on plan code
        couponsTobeDisplayed.forEach(coupon => {
          if (!groupedCoupons[coupon.plan_desc]) {
            groupedCoupons[coupon.plan_desc] = []
          }
          groupedCoupons[coupon.plan_desc].push(coupon)
        })
        for (let key of Object.keys(groupedCoupons)) {
          if (groupedCoupons[key].length === 1) {
            groupedCoupons[key][0].isDisabled = false
          }
        }
        console.log(
          '<<<<<< handleIncentivesByPlans() -Eligible Coupons - grouped Coupons >>>>>> :: ',
          groupedCoupons,
        )

        // if (!locationState?.isUpgrade || newMC || true) {
        setPlansIncentiveGroups(groupedCoupons)
        await validateAndApplyPlansIncentive(groupedCoupons)
        // }
      } else {
        setPlansIncentive([])
        setPlansIncentiveGroups({})
      }
    }
    handleIncentivesByPlans()
  }, [
    structuredCart,
    client,
    selectedCountry,
    validateAndApplyPlansIncentive,
    locationState?.isUpgrade,
    newMC,
  ])

  //  Combine all coupons for summary - dispatch to Redux as single source of truth
  useEffect(() => {
    dispatch(
      setCouponData({
        defaultObFeeCoupon: defaultObFeeCoupon,
        plansIncentive: plansIncentive,
        promotions: promotions,
      }),
    )
  }, [defaultObFeeCoupon, plansIncentive, promotions, dispatch])

  return {
    plansIncentiveGroups,
    allCoupons,
    removeCoupon,
    togglePlanIncentive,
    fetchAndApplyPromoCode,
  }
}
